import { usePersonReadDataContext } from '@aibees/elephant/context';
import {
  PeopleTableInfo,
  Person,
} from '@aibees/elephant/feature/search-table-people';
import {
  useWorkHistoryCurrentRolesInCompany,
  useWorkHistoryFullHistory,
  useWorkHistoryPresentRoles,
} from '@aibees/elephant/feature/search-table-people/components/people-drawer/components/contact-details/components/work-history';
import {
  getCurrentCompanyState,
  getPreviousCompanyState,
} from '@aibees/elephant/utils';
import { useMemo } from 'react';

export function useCompanyVisibilityState(
  personData?: Person | PeopleTableInfo | null
) {
  const personContext = usePersonReadDataContext();
  const dataToUse = personData ?? personContext?.api?.data ?? null;
  const currentRolesInCompany = useWorkHistoryCurrentRolesInCompany(dataToUse);
  const allPresentRoles = useWorkHistoryPresentRoles(dataToUse);
  const fullWorkHistory = useWorkHistoryFullHistory(dataToUse);

  return useMemo(() => {
    const current = getCurrentCompanyState(
      dataToUse as Person | null,
      currentRolesInCompany?.[0] ?? null
    );
    const previous = getPreviousCompanyState(fullWorkHistory, allPresentRoles);

    const shouldShowPreviousEmployment =
      previous.isNoLongerEmployed ||
      previous.hasOnlyPreviousCompanyName ||
      previous.noPreviousCompanyDataAvailable ||
      previous.wasPreviouslySelfEmployedOrFreelancer;

    const showNoDataAvailable =
      current.hasOnlyCompanyName ||
      current.noCompanyDataAvailable ||
      previous.hasOnlyPreviousCompanyName ||
      previous.noPreviousCompanyDataAvailable ||
      previous.wasPreviouslySelfEmployedOrFreelancer;

    const shouldShowCompanyInfoBadge =
      previous.hasOnlyPreviousCompanyName ||
      previous.noPreviousCompanyDataAvailable ||
      previous.wasPreviouslySelfEmployedOrFreelancer;

    const hasFullCompanyData =
      !current.hasOnlyCompanyName &&
      !current.noCompanyDataAvailable &&
      !current.isSelfEmployedOrFreelancer &&
      !previous.hasOnlyPreviousCompanyName &&
      !previous.noPreviousCompanyDataAvailable &&
      !previous.wasPreviouslySelfEmployedOrFreelancer;

    const currentCompanyDataItem =
      current.hasOnlyCompanyName ||
      previous.hasOnlyPreviousCompanyName ||
      previous.noPreviousCompanyDataAvailable ||
      previous.wasPreviouslySelfEmployedOrFreelancer;

    const displayCurrentCompanyButtons =
      current.noCompanyDataAvailable ||
      current.isSelfEmployedOrFreelancer ||
      previous.wasPreviouslySelfEmployedOrFreelancer;

    const companyNameHasData =
      current.isSelfEmployedOrFreelancer ||
      previous.wasPreviouslySelfEmployedOrFreelancer ||
      (!current.noCompanyDataAvailable &&
        !previous.noPreviousCompanyDataAvailable);

    return {
      ...current,
      ...previous,
      shouldShowPreviousEmployment,
      showNoDataAvailable,
      shouldShowCompanyInfoBadge,
      hasFullCompanyData,
      currentCompanyDataItem,
      displayCurrentCompanyButtons,
      companyNameHasData,
    };
  }, [dataToUse, currentRolesInCompany, allPresentRoles, fullWorkHistory]);
}
